<script lang="ts">
  import { getContext } from 'svelte';
  import RepositoryFactory, { MNP } from '../../../repositories/RepositoryFactory';
  import { loginUser } from '../../../components/auth/LoginUser';
  import Loading from '../../loader.svelte';
  import type {
    GetMnpReserveNumberCustomerResult,
    GetMnpReserveNumberForPremiumResult
  } from '../../../repositories/mnp';
  import { isAvailableMnpReserveNumberRequest } from '../../../components/Util.svelte';

  const { close }: any = getContext('simple-modal');

  let pageStatus: 'TOP' | 'REQUEST' = 'TOP';
  let isComplete = false;
  let promise = getMnpReserveNumberList();
  let requestPromise: Promise<GetMnpReserveNumberCustomerResult>;
  let cancelPromise: Promise<GetMnpReserveNumberForPremiumResult>;
  let issueStatusCode = '';
  $: disabledRequest = !isAvailableMnpReserveNumberRequest(issueStatusCode);
  $: disabledCancel = issueStatusCode !== 'ISSUED';

  async function getMnpReserveNumberList() {
    const mnpRepository = RepositoryFactory[MNP];
    return await mnpRepository
      .getMnpReserveNumberListForPremium()
      .then((res) => {
        if (res) {
          issueStatusCode = res[0]?.issueStatusCode;
        }
        return res;
      })
      .catch((error) => {
        if (error.response.status === 403) {
          displayErrorAlert();
        }
        throw new Error();
      });
  }

  async function getMnpReserveNumber() {
    const MnpRepository = RepositoryFactory[MNP];
    return await MnpRepository.getMnpReserveNumberForPremium().catch((error) => {
      if (error.response.status === 403) {
        displayErrorAlert();
      }
      throw new Error();
    });
  }

  async function getCustomer() {
    const mnpRepository = RepositoryFactory[MNP];
    return mnpRepository.getMnpReserveNumberCustomer().catch((error) => {
      if (error.response.status === 403) {
        displayErrorAlert();
      }
      throw new Error();
    });
  }

  function doRequest() {
    if (disabledRequest) {
      alert('MNP予約番号発行依頼はできません');
      return;
    }
    const MnpRepository = RepositoryFactory[MNP];
    MnpRepository.requestForPremium()
      .then((res) => {
        isComplete = true;
      })
      .catch((error) => {
        if (error.response.status === 403) {
          displayErrorAlert();
        } else {
          alert('MNP予約番号発行依頼に失敗しました');
        }
        throw new Error();
      });
  }

  function displayRequest() {
    pageStatus = 'REQUEST';
    isComplete = false;
    requestPromise = getCustomer();
  }

  function back() {
    pageStatus = 'TOP';
    promise = getMnpReserveNumberList();
  }

  function displayErrorAlert() {
    alert('セッションが切れました。再度ログインしてください。');
    loginUser.logout();
    location.reload();
  }
</script>

<div class="h-auto bg-white w-full m-auto">
  <div class="flex flex-col md:flex-row justify-between mb-10">
    <div class=" text-gray-700">
      <h1 class="text-2xl font-bold">MNP予約番号</h1>
    </div>

    {#if pageStatus === 'TOP'}
      <div class="flex justify-end flex-col md:flex-row pt-2">
        <button
          class="button-primary md:mr-3 text-sm mb-5"
          on:click={() => displayRequest()}
          disabled={disabledRequest}
        >
          予約番号発行
        </button>
      </div>
    {/if}
  </div>

  {#if pageStatus === 'TOP'}
    <div class="flex flex-col">
      <div class="overflow-x-auto">
        <div class="align-middle inline-block min-w-full">
          <div class="mb-6">
            <div class="container flex justify-between">
              <h2 class="text-xl font-bold text-gray-700">発行履歴</h2>
            </div>
          </div>

          <div class="shadow overflow-hidden border border-gray-200">
            <table class="min-w-full divide-y divide-gray-200 rounded-lg border-collapse">
              <thead class="bg-gray-100">
                <tr>
                  <th class="mnp-number-head"> 依頼状態 </th>
                  <th class="mnp-number-head"> 依頼日 </th>
                  <th class="mnp-number-head"> 有効期限日 </th>
                  <th class="mnp-number-head"> 予約番号 </th>
                </tr>
              </thead>
              {#await promise}
                <Loading />
              {:then mnpReserveNumberList}
                <tbody class="bg-white divide-y divide-gray-200">
                  {#each mnpReserveNumberList as mnpReserveNumber}
                    <tr>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {mnpReserveNumber.issueStatus}
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {mnpReserveNumber.requestDate}
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {mnpReserveNumber.expirationDate ?? '-'}
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {mnpReserveNumber.mnpNumber ?? '-'}
                      </td>
                    </tr>
                  {/each}
                </tbody>
              {/await}
            </table>
          </div>
        </div>
      </div>
    </div>
  {:else if pageStatus === 'REQUEST'}
    <div class="shadow rounded-lg bg-gray-50 border-1 p-4">
      <div class="flex flex-col">
        {#if !isComplete}
          {#await requestPromise}
            <Loading />
          {:then customer}
            <div class="inline-flex justify-items-center items-center min-w-full">
              MNP予約番号の発行依頼を行います。
            </div>
            <div class="flex-1 mt-4 mb-10">
              <div class="inline-flex justify-items-center items-center min-w-full">
                <div>電話番号：</div>
                <div>{customer.telNo}</div>
              </div>
              <div class="inline-flex justify-items-center items-center min-w-full">
                <div class="">氏名：</div>
                <div>
                  {customer.sei + customer.mei}
                </div>
              </div>
              <div class="inline-flex justify-items-center items-center min-w-full">
                <div class="">氏名ふりがな：</div>
                <div>
                  {customer.seiHiragana + customer.meiHiragana}
                </div>
              </div>
              <div class="inline-flex justify-items-center items-center min-w-full">
                <div class="">性別：</div>
                <div>{customer.sex}</div>
              </div>
            </div>
          {/await}
        {:else}
          <div class="flex-1 mt-4 mb-10">
            <div class="inline-flex justify-items-center items-center min-w-full">
              予約番号発行依頼を受け付けました。
            </div>
          </div>
        {/if}

        <div class="flex-1 mb-10">
          <div class="shadow rounded bg-gray-200 p-4 text-sm">
            <slot>
              ・予約番号が発行された際はご登録のメールアドレスに発行結果を通知いたします。<br />
              ・発行結果についてはマイページでも確認できます<br />
              ・予約番号の発行は9-20時が基本となり、この時間内の場合は概ね1時間後、時間外の場合は、上記時間内の発行となります<br
              />
              ・MNP予約番号を用いて他社へ転出されると、ご契約中のプラン等は自動的に解約となります。
            </slot>
          </div>
        </div>

        <div class="flex-1">
          <div class="flex flex-col justify-items-center items-center">
            <div class="flex-1">
              {#if isComplete}
                <button class="button-back" on:click={() => close()}>閉じる</button>
              {:else}
                <button class="button-request" on:click|once={() => doRequest()}> 実行 </button>
                <button class="button-back" on:click={() => back()}>戻る</button>
              {/if}
            </div>
          </div>
        </div>
      </div>
    </div>
  {/if}
</div>

<style lang="postcss">
  .button-primary {
    @apply bg-green-400 rounded hover:bg-green-500 text-white focus:outline-none px-4 py-2;
  }
  .button-primary:disabled {
    @apply opacity-30 cursor-default;
  }
  .button-request {
    @apply bg-green-400 rounded hover:bg-green-500 text-white focus:outline-none px-14 py-2;
  }
  .button-back {
    @apply bg-gray-300 rounded hover:bg-gray-400 text-gray-500 focus:outline-none px-14 py-2;
  }
  .mnp-number-head {
    @apply px-6 py-3 text-left text-sm text-gray-900;
  }
</style>
